import { useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FaExclamationTriangle, FaCheck, FaShieldAlt } from "react-icons/fa";

const reportCategories = [
  { value: "false-info", label: "False Information" },
  { value: "misleading", label: "Misleading Content" },
  { value: "manipulated", label: "Manipulated Media" },
  { value: "impersonation", label: "Impersonation" },
  { value: "conspiracy", label: "Conspiracy Theory" },
  { value: "other", label: "Other" },
];

const hackingCategories = [
  { value: "account", label: "Account Compromise" },
  { value: "website", label: "Website Defacement" },
  { value: "breach", label: "Data Breach" },
  { value: "malware", label: "Malware Attack" },
  { value: "other", label: "Other" },
];

const Report = () => {
  // Misinformation report state
  const [url, setUrl] = useState("");
  const [personName, setPersonName] = useState("");
  const [personEmail, setPersonEmail] = useState("");
  const [description, setDescription] = useState("");
  const [misinfoSource, setMisinfoSource] = useState("");
  const [dateObserved, setDateObserved] = useState("");
  const [supportingLinks, setSupportingLinks] = useState("");
  const [category, setCategory] = useState("false-info");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  
  // Hacking report state
  const [hackingType, setHackingType] = useState("account");
  const [hackingDate, setHackingDate] = useState("");
  const [hackingDescription, setHackingDescription] = useState("");
  const [affectedSystems, setAffectedSystems] = useState("");
  const [perpetratorInfo, setPerpetratorInfo] = useState("");
  const [evidenceDetails, setEvidenceDetails] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  
  // Shared state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState("misinfo");
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (file: File | null) => void) => {
    const file = e.target.files?.[0] || null;
    setter(file);
  };

  const handleMisinfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url || !description || !category) {
      toast({
        title: "Missing information",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }
    
    setSubmitting(true);
    
    // Simulate API call with timeout
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast({
        title: "Report submitted",
        description: "Thank you for helping combat misinformation.",
        variant: "default",
      });
    }, 1500);
  };

  const handleHackingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hackingDescription || !hackingType || !hackingDate || !affectedSystems) {
      toast({
        title: "Missing information",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }
    
    setSubmitting(true);
    
    // Simulate API call with timeout
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast({
        title: "Hacking report submitted",
        description: "Thank you for reporting this security incident.",
        variant: "default",
      });
    }, 1500);
  };

  const handleReset = () => {
    setUrl("");
    setPersonName("");
    setPersonEmail("");
    setDescription("");
    setMisinfoSource("");
    setDateObserved("");
    setSupportingLinks("");
    setCategory("false-info");
    setScreenshot(null);
    setHackingType("account");
    setHackingDate("");
    setHackingDescription("");
    setAffectedSystems("");
    setPerpetratorInfo("");
    setEvidenceDetails("");
    setEvidenceFile(null);
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-4 px-1 sm:px-2 md:px-8 lg:px-16">
      <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-6 sm:space-y-8 px-1 sm:px-2 md:px-8 lg:px-12">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-truth">Report Misinformation</h1>
          <p className="mt-2 text-gray-400 text-sm sm:text-base">Submit and track suspicious content for community verification</p>
        </div>

        {submitted ? (
          <Card className="bg-black/20 backdrop-blur-sm border-green-500/30 animate-fade-in rounded-md sm:rounded-lg">
            <CardContent className="pt-6 pb-6 flex flex-col items-center text-center">
              <div className="rounded-full bg-green-500/20 p-3 mb-4">
                <FaCheck className="h-6 w-6 text-green-500" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold mb-2">Report Successfully Submitted</h2>
              <p className="text-gray-400 mb-6 max-w-lg text-sm sm:text-base">
                Thank you for your contribution. Our team will review your report and take appropriate action.
              </p>
              <div className="flex space-x-4">
                <Button onClick={handleReset}>Submit Another Report</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="misinfo" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 text-xs sm:text-sm">
              <TabsTrigger value="misinfo">Report Misinformation</TabsTrigger>
              <TabsTrigger value="hacking">Report Hacking Incident</TabsTrigger>
            </TabsList>
            
            <TabsContent value="misinfo">
              <form onSubmit={handleMisinfoSubmit}>
                <Card className="bg-black/20 backdrop-blur-sm border-gray-800 rounded-md sm:rounded-lg p-3 sm:p-6">
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg">Misinformation Report Details</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Provide information about the misinformation you encountered.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 sm:space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="url">Content URL <span className="text-red-500">*</span></Label>
                      <Input
                        id="url"
                        placeholder="https://example.com/article"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        required
                      />
                      <p className="text-xs text-gray-400">
                        The direct link to the content containing potential misinformation.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="personName">Reporter's Name (Optional)</Label>
                        <Input
                          id="personName"
                          placeholder="Your name"
                          value={personName}
                          onChange={(e) => setPersonName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="personEmail">Reporter's Email (Optional)</Label>
                        <Input
                          id="personEmail"
                          placeholder="Your email"
                          value={personEmail}
                          onChange={(e) => setPersonEmail(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
                      <RadioGroup
                        value={category}
                        onValueChange={setCategory}
                        className="grid grid-cols-1 md:grid-cols-2 gap-2"
                      >
                        {reportCategories.map((cat) => (
                          <div key={cat.value} className="flex items-center space-x-2">
                            <RadioGroupItem value={cat.value} id={cat.value} />
                            <Label htmlFor={cat.value} className="cursor-pointer">
                              {cat.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Detailed Description <span className="text-red-500">*</span></Label>
                      <Textarea
                        id="description"
                        placeholder="Please describe what's misleading or false about this content..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        required
                      />
                      <p className="text-xs text-gray-400">
                        Explain why you believe this content is misinformation and any evidence you have.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="misinfoSource">Source of Misinformation</Label>
                      <Textarea
                        id="misinfoSource"
                        placeholder="Where did this misinformation originate from or how is it being spread?"
                        value={misinfoSource}
                        onChange={(e) => setMisinfoSource(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dateObserved">Date & Time Observed</Label>
                      <Input
                        id="dateObserved"
                        placeholder="YYYY-MM-DD HH:MM or approximate date"
                        value={dateObserved}
                        onChange={(e) => setDateObserved(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="supportingLinks">Supporting Evidence Links</Label>
                      <Textarea
                        id="supportingLinks"
                        placeholder="Paste any links to supporting evidence or fact-checks"
                        value={supportingLinks}
                        onChange={(e) => setSupportingLinks(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="screenshot">Screenshot (optional)</Label>
                      <Input
                        id="screenshot"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, setScreenshot)}
                      />
                      <p className="text-xs text-gray-400">
                        Upload a screenshot of the content if available. This helps us verify the report.
                      </p>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between border-t border-gray-800 pt-6">
                    <p className="text-sm text-gray-400">
                      <span className="text-red-500">*</span> Required fields
                    </p>
                    <Button type="submit" disabled={submitting} className="ml-auto flex items-center gap-2">
                      {submitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaExclamationTriangle className="h-4 w-4 mr-2" /> Submit Report
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </form>
            </TabsContent>

            <TabsContent value="hacking">
              <form onSubmit={handleHackingSubmit}>
                <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
                  <CardHeader>
                    <CardTitle>Hacking Incident Report</CardTitle>
                    <CardDescription>
                      Report cybersecurity incidents, breaches, or suspicious activities.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="hackingType">Type of Hacking Incident <span className="text-red-500">*</span></Label>
                      <RadioGroup
                        value={hackingType}
                        onValueChange={setHackingType}
                        className="grid grid-cols-1 md:grid-cols-2 gap-2"
                      >
                        {hackingCategories.map((cat) => (
                          <div key={cat.value} className="flex items-center space-x-2">
                            <RadioGroupItem value={cat.value} id={`hacking-${cat.value}`} />
                            <Label htmlFor={`hacking-${cat.value}`} className="cursor-pointer">
                              {cat.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hackingDate">Date & Time of Incident <span className="text-red-500">*</span></Label>
                      <Input
                        id="hackingDate"
                        placeholder="YYYY-MM-DD HH:MM or approximate date/time"
                        value={hackingDate}
                        onChange={(e) => setHackingDate(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hackingDescription">Detailed Description of Hacking Incident <span className="text-red-500">*</span></Label>
                      <Textarea
                        id="hackingDescription"
                        placeholder="Please describe what happened in detail..."
                        value={hackingDescription}
                        onChange={(e) => setHackingDescription(e.target.value)}
                        rows={4}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="affectedSystems">Systems/Accounts Affected <span className="text-red-500">*</span></Label>
                      <Textarea
                        id="affectedSystems"
                        placeholder="What systems, accounts, or services were affected?"
                        value={affectedSystems}
                        onChange={(e) => setAffectedSystems(e.target.value)}
                        rows={3}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="perpetratorInfo">Known Perpetrator Information (Optional)</Label>
                      <Textarea
                        id="perpetratorInfo"
                        placeholder="Any information about the suspected perpetrators if known"
                        value={perpetratorInfo}
                        onChange={(e) => setPerpetratorInfo(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="evidenceDetails">Supporting Evidence/Logs</Label>
                      <Textarea
                        id="evidenceDetails"
                        placeholder="Describe any evidence or paste logs here"
                        value={evidenceDetails}
                        onChange={(e) => setEvidenceDetails(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="evidenceFile">Evidence File (optional)</Label>
                      <Input
                        id="evidenceFile"
                        type="file"
                        accept=".pdf,.txt,.log,.zip,.jpg,.png"
                        onChange={(e) => handleFileChange(e, setEvidenceFile)}
                      />
                      <p className="text-xs text-gray-400">
                        Upload evidence files such as logs, screenshots, or documents (max 10MB).
                      </p>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between border-t border-gray-800 pt-6">
                    <p className="text-sm text-gray-400">
                      <span className="text-red-500">*</span> Required fields
                    </p>
                    <Button type="submit" disabled={submitting} className="ml-auto flex items-center gap-2">
                      {submitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaShieldAlt className="h-4 w-4 mr-2" /> Submit Incident Report
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </form>
            </TabsContent>
          </Tabs>
        )}

        <div className="space-y-4 mt-8">
          <h2 className="text-xl font-bold">What Happens Next?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InfoCard
              step="1"
              title="Report Review"
              description="Our team reviews the submitted report to verify the content and assess the nature of the issue."
            />
            <InfoCard
              step="2"
              title="Analysis"
              description="We use AI tools and human experts to analyze the report against reliable sources and data."
            />
            <InfoCard
              step="3"
              title="Action Taken"
              description="Verified issues are added to our database to improve detection and shared with relevant authorities when necessary."
            />
          </div>
        </div>

        <Card className="bg-black/20 backdrop-blur-sm border-gray-800 mt-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="rounded-full bg-truth/20 p-3 shrink-0">
                <FaExclamationTriangle className="h-6 w-6 text-truth" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">See Something? Say Something</h3>
                <p className="text-gray-400">
                  By reporting misinformation and security incidents, you're helping create a more trustworthy and secure digital environment.
                  Your reports directly improve our AI detection models and help protect others.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

interface InfoCardProps {
  step: string;
  title: string;
  description: string;
}

function InfoCard({ step, title, description }: InfoCardProps) {
  return (
    <div className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-4">
      <div className="flex items-center mb-3">
        <span className="rounded-full bg-truth/20 w-6 h-6 flex items-center justify-center text-xs font-bold text-truth mr-2">
          {step}
        </span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-gray-400">{description}</p>
    </div>
  );
}

export default Report;
